import { DateTime, WeekdayNumbers } from 'luxon';
import React, { useEffect, useState } from 'react';

import { LocalizedTime } from '../../helpers/dates';
import { OpenCloseHoursWeekdays } from './open-close-hours-weekdays.interface';

interface OpeningHoursCompactProps {
  openingHoursArray: OpenCloseHoursWeekdays[];
  node_locale: string;
  timezone: string;
  openingHoursClosed: string;
}

export const OpeningHoursCompact = ({
  openingHoursArray,
  node_locale,
  timezone,
  openingHoursClosed,
}: OpeningHoursCompactProps) => {
  const [openingHoursStrings, setOpeningHoursStrings] = useState<string[]>();

  useEffect(() => {
    const luxonWeekdayFormatString = 'cccc';
    const titleCaseString = (string: string): string => {
      return string
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    };

    const reducedHours = openingHoursArray?.reduce<
      Array<OpenCloseHoursWeekdays>
    >((accumulated, current, index) => {
      // index starts at 0, index + 1 represent weekdays 1 - 7
      const weekdayNumber = index + 1;
      const foundIndex = accumulated?.findIndex(
        (hours) => hours.open === current.open && hours.close === current.close,
      );
      if (foundIndex > -1) {
        //Test to see if index + 1 is far away from other days....
        if (
          accumulated[foundIndex].weekDays.includes(weekdayNumber - 1) ||
          accumulated[foundIndex].weekDays.includes(weekdayNumber + 1)
        ) {
          accumulated[foundIndex].weekDays.push(weekdayNumber);
        } else if (
          !accumulated[foundIndex].weekDays.includes(weekdayNumber - 1) ||
          !accumulated[foundIndex].weekDays.includes(weekdayNumber + 1)
        ) {
          accumulated.push(current);
        }
      } else {
        accumulated.push(current);
      }
      return accumulated;
    }, []);

    const hoursStrings = reducedHours?.map((openingHours) => {
      const isClosed =
        openingHours.open === 'Closed' || openingHours.close === 'Closed';
      const temporaryDateTime = DateTime.now()
        .setLocale(node_locale)
        .setZone(timezone);

      if (openingHours.weekDays.length > 1) {
        const fromDay: WeekdayNumbers = openingHours
          .weekDays[0] as WeekdayNumbers;
        const toDay: WeekdayNumbers = openingHours.weekDays[
          openingHours.weekDays.length - 1
        ] as WeekdayNumbers;

        const openHoursString = `${titleCaseString(
          temporaryDateTime
            .set({ weekday: fromDay })
            .toFormat(luxonWeekdayFormatString),
        )} - ${titleCaseString(
          temporaryDateTime
            .set({ weekday: toDay })
            .toFormat(luxonWeekdayFormatString),
        )}: ${
          !isClosed
            ? `${LocalizedTime(
                openingHours.open,
                node_locale,
              )} - ${LocalizedTime(openingHours.close, node_locale)}`
            : openingHoursClosed
        }`;

        return openHoursString;
      } else if (openingHours.weekDays.length === 1) {
        const singleDay: WeekdayNumbers = openingHours
          .weekDays[0] as WeekdayNumbers;

        const openHoursString = `${titleCaseString(
          temporaryDateTime
            .set({ weekday: singleDay })
            .toFormat(luxonWeekdayFormatString),
        )}: ${
          !isClosed
            ? `${LocalizedTime(
                openingHours.open,
                node_locale,
              )} - ${LocalizedTime(openingHours.close, node_locale)}`
            : openingHoursClosed
        }`;

        return openHoursString;
      }

      // Handle case when no weekdays are present if needed

      return '';
    });

    setOpeningHoursStrings(hoursStrings);
  }, [
    setOpeningHoursStrings,
    node_locale,
    openingHoursArray,
    timezone,
    openingHoursClosed,
  ]);

  return (
    <ul>
      {openingHoursStrings?.map((s, index) => (
        <li key={`open-hours-${index}`}>{s}</li>
      ))}
    </ul>
  );
};
